import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

window.initFirstVideoFixed = function initFirstVideoFixed() {
    const section = document.querySelector('.first-sec.index-page');
    if (!section) return;

    const bgVideo = section.querySelector('.first-sec__bg-video');
    if (!bgVideo) return;

    const wrap = section.querySelector('.first-sec__wrap');
    const videoEl = bgVideo.querySelector('video');
    const overlay = bgVideo.querySelector('.first-sec__bg-video-overlay');
    const nextSection = section.nextElementSibling;
    const hasStaticBg = bgVideo.classList.contains('not-animation');

    section.classList.toggle('has-static-bg', hasStaticBg);

    const relatedElements = [wrap, videoEl, overlay].filter(Boolean);

    const resetAnimatedState = () => {
        bgVideo.classList.remove('is-fixed');
        bgVideo.style.visibility = '';

        relatedElements.forEach((element) => {
            gsap.set(element, {
                clearProps: 'transform,opacity,visibility',
            });
        });
    };

    const killRelatedScrollTriggers = () => {
        ScrollTrigger.getAll().forEach((triggerInstance) => {
            if (
                triggerInstance.trigger === section ||
                triggerInstance.trigger === nextSection ||
                triggerInstance.endTrigger === section ||
                triggerInstance.endTrigger === nextSection
            ) {
                triggerInstance.kill();
            }
        });
    };

    if (hasStaticBg) {
        gsap.killTweensOf(relatedElements);
        killRelatedScrollTriggers();
        resetAnimatedState();
        return;
    }

    // Fix video in viewport from when section hits top until next section covers it
    ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        ...(nextSection
            ? { endTrigger: nextSection, end: 'top top' }
            : { end: 'bottom top' }),
        toggleClass: { targets: bgVideo, className: 'is-fixed' },
    });

    if (nextSection) {
        // Hide video when next section fully covers it
        ScrollTrigger.create({
            trigger: nextSection,
            start: 'top top',
            onEnter: () => {
                bgVideo.style.visibility = 'hidden';
            },
            onLeaveBack: () => {
                bgVideo.style.visibility = '';
            },
        });
    }

    // --- RESPONSIVE ANIMATIONS ---
    let mm = gsap.matchMedia();

    // Desktop (Landscape & Width > 640px)
    mm.add("(min-width: 641px) and (orientation: landscape)", () => {
        // Parallax on wrap
        if (wrap) {
            gsap.to(wrap, {
                yPercent: 120,
                ease: 'none',
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: true,
                },
            });
        }

        if (!nextSection) return;

        // Scale + darken when next section starts covering ~15% of viewport
        const scaleDarkenTl = gsap.timeline({
            scrollTrigger: {
                trigger: nextSection,
                start: 'top 85%',
                end: 'top 25%',
                scrub: true,
            },
        });

        if (videoEl) scaleDarkenTl.to(videoEl, { scale: 1.25, ease: 'none' }, 0);
        if (overlay) scaleDarkenTl.to(overlay, { opacity: 0.65, ease: 'none' }, 0);

        // Fade out content when video is ~halfway through its scale
        if (wrap) {
            gsap.to(wrap, {
                opacity: 0,
                ease: 'none',
                scrollTrigger: {
                    trigger: nextSection,
                    start: 'top 75%',
                    end: 'top 25%',
                    scrub: true,
                },
            });
        }
    });

    // Tablet Portrait (min-width: 641px and orientation: portrait)
    mm.add("(min-width: 641px) and (orientation: portrait)", () => {
        // Parallax on wrap
        if (wrap) {
            gsap.to(wrap, {
                yPercent: 50, // Less parallax due to smaller height
                ease: 'none',
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: true,
                },
            });
        }

        if (!nextSection) return;

        // Scale + darken MUCH LATER because section goes from ~150vh to auto/800px!
        const scaleDarkenTl = gsap.timeline({
            scrollTrigger: {
                trigger: nextSection,
                start: 'top 30%',
                end: 'top top',
                scrub: true,
            },
        });

        if (videoEl) scaleDarkenTl.to(videoEl, { scale: 1.15, ease: 'none' }, 0);
        if (overlay) scaleDarkenTl.to(overlay, { opacity: 0.65, ease: 'none' }, 0);

        // Fade out content
        if (wrap) {
            gsap.to(wrap, {
                opacity: 0,
                ease: 'none',
                scrollTrigger: {
                    trigger: nextSection,
                    start: 'top 20%',
                    end: 'top top',
                    scrub: true,
                },
            });
        }
    });

    // Mobile (max-width: 640px) - Section is 130vh, more scroll space
    mm.add("(max-width: 640px)", () => {
        // Parallax on wrap
        if (wrap) {
            gsap.to(wrap, {
                yPercent: 80, // More parallax than tablet
                ease: 'none',
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: true,
                },
            });
        }

        if (!nextSection) return;

        // Scale + darken starts EARLIER due to 130vh height
        const scaleDarkenTl = gsap.timeline({
            scrollTrigger: {
                trigger: nextSection,
                start: 'top 75%', // Starts earlier compared to desktop (85%) because section is 130vh not 150vh, but much earlier than tablet
                end: 'top 15%',
                scrub: true,
            },
        });

        if (videoEl) scaleDarkenTl.to(videoEl, { scale: 1.25, ease: 'none' }, 0);
        if (overlay) scaleDarkenTl.to(overlay, { opacity: 0.65, ease: 'none' }, 0);

        // Fade out content starts earlier too
        if (wrap) {
            gsap.to(wrap, {
                opacity: 0,
                ease: 'none',
                scrollTrigger: {
                    trigger: nextSection,
                    start: 'top 65%', // Early fade out
                    end: 'top 15%',
                    scrub: true,
                },
            });
        }
    });
};

initFirstVideoFixed();
